import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Tabs,
  Tab,
  Button,
  Paper,
} from '@mui/material';
import { useWeb3React } from '@web3-react/core';
import {
  createKYCContract, 
  isSupportedNetwork, 
  NETWORKS,
  callContractWithRetry,
  handleContractError,
  getPolygonGasFees
} from '../config/contracts';
import { ethers } from 'ethers';
import { Base64 } from 'js-base64'; 
import { toast } from 'react-toastify';
import { BasicInfo, KYCLevel1, KYCLevel2, KYCTemplateType, KYCSubmissionStatus } from '../types/kyc';
import { validateFile, FILE_CONSTRAINTS, fetchFromIPFS, getpinatakey, saveblobContentfromjwk, savemetaContentfromjwk } from '../utils/ipfs';
import BasicInfoForm from '../components/BasicInfoForm';
import KYCLevel1Form from '../components/KYCLevel1Form';
import KYCLevel2Form from '../components/KYCLevel2Form';
import SignMessageDialog from '../components/SignMessageDialog';
import { useSignMessageStore } from '../stores/signMessageStore';
import PermissionGuard from '../components/PermissionGuard';
import { getKYCStatusText, getKYCStatusColor, isAddressEqual } from '../utils/walletUtils';
import useSettings from '../hooks/useSettings';
import { useLogincheck } from '../hooks/auth';
import { ownerAddress } from '../config/contracts-address';
import Checksign from '../components/Checksign';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kyc-tabpanel-${index}`}
      aria-labelledby={`kyc-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Accessibility properties for Tabs
function a11yProps(index: number) {
  return {
    id: `kyc-tab-${index}`,
    'aria-controls': `kyc-tabpanel-${index}`,
  };
}

const AssetsMintPage: React.FC = () => {
  return   <PermissionGuard requireConnection><AssetsMintContent /></PermissionGuard>;
};

const AssetsMintContent: React.FC = () => {
  const { account, provider, chainId } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userTokenId, setUserTokenId] = useState<string | null>(null);
  const { auths, activeuser,authjwk   } = useSettings(); 
  const { loginauthkycuser } = useLogincheck(); 
  // SignMessageDialog状态管理
  const {
    setOpen,
    setTitle,
    setSteps,
    setActiveStep,
    setMessage,
    setLoading: setDialogLoading,
    setError: setDialogError,
    nextStep,
    loading:dialogLoading,
    reset: resetDialog
  } = useSignMessageStore();
  
  // Tab相关状态
  const [activeTab, setActiveTab] = useState(0);
  
  // 表单数据状态
  const [basicInfo, setBasicInfo] = useState<BasicInfo>({});
  const [kycLevel1, setKycLevel1] = useState<KYCLevel1>({});
  const [kycLevel2, setKycLevel2] = useState<KYCLevel2>({});
  
  // 用户KYC状态
  const [userKYCStatus, setUserKYCStatus] = useState<KYCSubmissionStatus[]>([]);

  useEffect(() => {
    if (account && provider && chainId) {
      loadUserData();
    }
  }, [account, provider, chainId]);

  const endsubmitjob = async ()=>{
      loadUserData();
      setOpen(false);
      resetDialog();
  }

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!chainId || !isSupportedNetwork(chainId)) {
        throw new Error('Please switch to a supported network');
      }

      if (!provider || !account) return;
      
      // 使用需要签名的合约实例
      const signer = provider.getSigner();
      const contract = createKYCContract.writable(chainId, signer);
      if (!contract) {
        throw new Error('Failed to get contract instance');
      }

      // 1. 获取用户Token ID
      const tokenId = await callContractWithRetry(() => contract.getTokenIdByWallet(account)) as any;
      const tokenIdString = tokenId.toString();
      
      if (tokenIdString !== '0') {
        setUserTokenId(tokenIdString);
        
        // 2. 如果用户有Token，获取KYC状态
        const statusList: KYCSubmissionStatus[] = [];
        
        // 检查三种KYCForm的状态 (1, 2, 3)
        for (let templateId = 1; templateId <= 3; templateId++) {
          try {
            // 使用需要签名的 getMyKYCData 接口
            const result = await callContractWithRetry(() => contract.getMyKYCData(templateId)) as any;
            console.log("getMyKYCData:",result);
            statusList.push({
              templateId,
              status: result.status, // 从getMyKYCData获取状态
              ipfsHash: result.ipfsHash
            });
          } catch (err) {
            // 如果获取失败，说明该Form还没有提交
            statusList.push({
              templateId,
              status: 0, // NONE
            });
          }
        }
        
        setUserKYCStatus(statusList);
        loadKYCData(statusList);
        setLoading(false);
      } else {
        setUserTokenId(null);
        setUserKYCStatus([]);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to load user data:', err);
      setError(handleContractError(err));
    } finally {
      setLoading(false);
    }
  };


    // 加载KYC数据
    const loadKYCData = async (statuses: KYCSubmissionStatus[]) => {
      try {
        // 加载基本信息 (Form0)
        const basicInfoStatus = statuses.find(s => s.templateId === 1);
        if (basicInfoStatus?.ipfsHash) {
          const basicInfoData = await fetchFromIPFS(basicInfoStatus.ipfsHash);
          setBasicInfo(basicInfoData);
        }
  
        // 加载KYC Level 1 (Form1)
        const level1Status = statuses.find(s => s.templateId === 2);
        if (level1Status?.ipfsHash) {
          const level1Data =JSON.parse(Base64.decode(level1Status.ipfsHash));
          setKycLevel1(level1Data);
        }
  
        // 加载KYC Level 2 (Form2)
        const level2Status = statuses.find(s => s.templateId === 3);
        if (level2Status?.ipfsHash) {
          const level2Data =JSON.parse(Base64.decode(level2Status.ipfsHash));
          setKycLevel2(level2Data);
        }
      } catch (err) {
        console.error('Failed to load KYC data:', err);
      }
    };

  const handleProfileNFT = async(data: BasicInfo, files: { image?: File; banner?: File }) => {
            // console.log("formik onSubmit:",values)  
            if(userTokenId){
              handleEditWithBasicInfo(data,files); 
            }else{
              handleMintWithBasicInfo(data,files); 
            } 
            
        } 

  const handleMintWithBasicInfo = async (data: BasicInfo, files: { image?: File; banner?: File }) => {
    if (!account || !provider || !chainId) {
      toast.error('Please connect wallet first');
      return;
    }

    if (userTokenId) {
      toast.error('You already have KYC, no need to mint again');
      return;
    }

    // 验证BasicInfo数据
    if (!data.name && !data.email) {
      toast.error('Please fill in at least name or email');
      return;
    }

    const signer = provider.getSigner();
    const contract = createKYCContract.writable(chainId, signer);
    if (!contract) {
      toast.error('Failed to get contract instance');
      return; 
    } 

    // 设置步骤器
    setTitle('Mint KYC');
    setSteps([
      'Validate Form Data',
      'Upload Files',  
      'Submit Transaction',
      'Wait for Confirmation'
    ]);
    setActiveStep(0);
    setOpen(true);
    setDialogError(null);

    try {

        const pinataapikey = await getpinatakey(account,5,authjwk);
        if(!pinataapikey?.JWT){
          const message = "Something went wrong about network!";
          setDialogError(message); 
          setMessage('operation fail! all operations not completed');
          setDialogLoading(false);    
          toast.error("check your network!"); 
        }  
         
      // 步骤1: 验证表单数据
      setMessage('Validating form data...');
      setDialogLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // 模拟验证时间
      nextStep();
       // 步骤2: 上传文件到IPFS
       setMessage('Uploading files...');  
        if(files.image){  
            data.image = await saveblobContentfromjwk(files.image,pinataapikey?.JWT);
          } 
          if(files.banner){  
            data.banner = await saveblobContentfromjwk(files.banner,pinataapikey?.JWT);
          }  
        const newmetadataipfs = await savemetaContentfromjwk(data,pinataapikey?.JWT); 
      
     if (newmetadataipfs) { 
     
      nextStep(); 
        // 根据环境变量决定是否进行 gas 估算
        let gasOptions: any = {}; 

        // 步骤4: 提交交易
        setMessage('Submitting transaction...');
        const tx = await callContractWithRetry(() => contract.mintKYCNFT(newmetadataipfs, gasOptions)) as any;
        nextStep();

        // 步骤5: 等待确认
        setMessage('Waiting for confirmation...');
        const receipt = await tx.wait();
        
        if (receipt.status === 1) {
          setMessage('KYC minted successfully!');
          setDialogLoading(false); 
          // 重新加载用户数据
        //  await loadUserData();  
          toast.success('KYC minted successfully!');
        } else {
          throw new Error('Transaction failed');
        }

       } else {
        throw new Error('Transaction failed');
      }
    } catch (err: any) {
      console.error('Mint failed:', err);
      setDialogLoading(false);
      
      let errorMessage = 'Mint failed';
      if (err.code === 4001) {
        errorMessage = 'User cancelled transaction';
      } else if (err.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient balance';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setDialogError(errorMessage);
      toast.error(errorMessage);
    }
  };
 
    const handleEditWithBasicInfo = async (data: BasicInfo, files: { image?: File; banner?: File }) => {
     if (!account || !provider || !chainId) {
       toast.error('Please connect wallet first');
       return;
     }

     if (!userTokenId) {
       toast.error('Please mint KYC NFT first');
       return;
     }

     // Validate data
     if (!data.name || !data.email) {
       toast.error('Please fill in name and email');
       return;
     }

    const signer = provider.getSigner();
    const contract = createKYCContract.writable(chainId, signer);
    if (!contract) {
      toast.error('Failed to get contract instance');
      return; 
    } 

     // Set stepper
      setTitle('Submit KYC Level 1 Data');
      setSteps([
        'Validate Form Data',
        'Upload Files',  
        'Submit Transaction',
        'Wait for Confirmation'
      ]);
      setActiveStep(0);
      setOpen(true);
      setDialogError(null);

     try {
        const pinataapikey = await getpinatakey(account,5,authjwk);
        if(!pinataapikey?.JWT){
          const message = "Something went wrong about network!";
          setDialogError(message); 
          setMessage('operation fail! all operations not completed');
          setDialogLoading(false);    
          toast.error("check your network!"); 
        }  
       setDialogLoading(true);
        nextStep();
       // Step 2: Upload files to IPFS
       setMessage('Uploading files...');
        
        if(files.image){  
            data.image = await saveblobContentfromjwk(files.image,pinataapikey?.JWT);
          } 
          if(files.banner){  
            data.banner = await saveblobContentfromjwk(files.banner,pinataapikey?.JWT);
          }  
        const newmetadataipfs = await savemetaContentfromjwk(data,pinataapikey?.JWT); 
      
        if (newmetadataipfs) {  
            
          nextStep();   
          
          // Set gas options based on environment variables
          let gasOptions: any = {}; 

            // Step 3: Submit transaction
            setMessage('Submitting transaction...');
            const tx = await callContractWithRetry(() => contract.submitKYCData(1,newmetadataipfs, gasOptions)) as any;
            
            nextStep();

            // Step 4: Wait for confirmation
            setMessage('Waiting for transaction confirmation...');
            const receipt = await tx.wait();
            
            if (receipt.status === 1) {
              setMessage(`KYC Form 1 ${receipt.status} submitted successfully!`);
              setDialogLoading(false); 
              // Reload user data
            // await loadUserData();    
              toast.success(`KYC Form 1 ${receipt.status} submitted successfully!`);
            } else {
              throw new Error('Transaction failed');
            }
         } else {
          throw new Error('Transaction failed');
        }
       
     } catch (err: any) {
        console.error('KYC Level 1 submission failed:', err);
        setDialogLoading(false);
        
        let errorMessage = 'Submission failed';
        if (err.code === 4001) {
          errorMessage = 'User cancelled transaction';
        } else if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient balance';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setDialogError(errorMessage);
        toast.error(errorMessage);
      }
   };


   const handleMintWithKYCLevel1 = async (data: KYCLevel1, files: { idFrontImage?: File; idBackImage?: File }) => {
     if (!account || !provider || !chainId) {
       toast.error('Please connect wallet first');
       return;
     }

     if (!userTokenId) {
       toast.error('Please mint KYC NFT first');
       return;
     }

     // Validate data
     if (!data.realName || !data.idNumber) {
       toast.error('Please fill in real name and ID number');
       return;
     }
    const signer = provider.getSigner();
    const contract = createKYCContract.writable(chainId, signer);
    if (!contract) {
      toast.error('Failed to get contract instance');
      return; 
    } 
     // Set stepper
      setTitle('Submit KYC Level 2 Data');
      setSteps([
        'Validate Form Data',
        'Upload Files', 
        'Submit Transaction',
        'Wait for Confirmation'
      ]);
      setActiveStep(0);
      setOpen(true);
      setDialogError(null);

     try {
         const pinataapikey = await getpinatakey(account,5,authjwk);
        if(!pinataapikey?.JWT){
          const message = "Something went wrong about network!";
          setDialogError(message); 
          setMessage('operation fail! all operations not completed');
          setDialogLoading(false);    
          toast.error("check your network!"); 
         }  
         setDialogLoading(true);
          nextStep();
         setMessage('Uploading files...');
          if(files.idFrontImage){  
            data.idFrontImage = await saveblobContentfromjwk(files.idFrontImage,pinataapikey?.JWT);
          } 
          if(files.idBackImage){  
            data.idBackImage = await saveblobContentfromjwk(files.idBackImage,pinataapikey?.JWT);
          }  
         const  LocalencryptedFiles= Base64.encode(JSON.stringify(data));
    
       if (LocalencryptedFiles) {  
        nextStep();
        
        // Set gas options based on environment variables
        let gasOptions: any = {}; 
          // Step 3: Submit transaction
          setMessage('Submitting transaction...');
          const tx = await callContractWithRetry(() => contract.submitKYCData(2, LocalencryptedFiles, gasOptions)) as any;
          
          nextStep();

          // Step 4: Wait for confirmation
          setMessage('Waiting for transaction confirmation...');
          const receipt = await tx.wait();
        // console.log("receipt.status:", receipt.status)
          if (receipt.status === 1) {
            setMessage(`KYC Form 2 submitted successfully!`);
            setDialogLoading(false); 
            // Reload user data
          // await loadUserData();  
            
            toast.success(`KYC Form 2 submitted successfully!`);
          } else {
            throw new Error('Transaction failed');
          }

       } else {
          throw new Error('Transaction failed');
      }

       
     } catch (err: any) {
        console.error('KYC Level 2 submission failed:', err);
        setDialogLoading(false);
        
        let errorMessage = 'Submission failed';
        if (err.code === 4001) {
          errorMessage = 'User cancelled transaction';
        } else if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient balance';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setDialogError(errorMessage);
        toast.error(errorMessage);
      }
   };

   const handleMintWithKYCLevel2 = async (data: KYCLevel2, files: { videoFile: File | null }) => {
     if (!account || !provider || !chainId) {
       toast.error('Please connect wallet first');
       return;
     }

     if (!userTokenId) {
       toast.error('Please mint KYC NFT first');
       return;
     }

     // Validate data
     if (!data.randomNumber || !files.videoFile) {
       toast.error('Please fill in random number and upload video');
       return;
     }

    const signer = provider.getSigner();
    const contract = createKYCContract.writable(chainId, signer);
    if (!contract) {
      toast.error('Failed to get contract instance');
      return; 
    } 
     // Set stepper
      setTitle('Submit KYC Level 3 Data');
      setSteps([
        'Validate Form Data',
        'Upload Files', 
        'Submit Transaction',
        'Wait for Confirmation'
      ]);
      setActiveStep(0);
      setOpen(true);
      setDialogError(null);

     try {
         const pinataapikey = await getpinatakey(account,5,authjwk);
        if(!pinataapikey?.JWT){
          const message = "Something went wrong about network!";
          setDialogError(message); 
          setMessage('operation fail! all operations not completed');
          setDialogLoading(false);    
          toast.error("check your network!"); 
         }  
         setDialogLoading(true);
          nextStep();
         setMessage('Uploading files...');
          if(files.videoFile){  
            data.videoUrl = await saveblobContentfromjwk(files.videoFile,pinataapikey?.JWT);
          } 
       
         const  LocalencryptedFiles= Base64.encode(JSON.stringify(data));
    
          if (LocalencryptedFiles) {  
        nextStep();
        
        // Set gas options based on environment variables
        let gasOptions: any = {}; 
          // Step 3: Submit transaction
          setMessage('Submitting transaction...');
          const tx = await callContractWithRetry(() => contract.submitKYCData(3, LocalencryptedFiles, gasOptions)) as any;
          
          nextStep();

          // Step 4: Wait for confirmation
          setMessage('Waiting for transaction confirmation...');
          const receipt = await tx.wait();
        // console.log("receipt.status:", receipt.status)
          if (receipt.status === 1) {
            setMessage(`KYC Form 3 submitted successfully!`);
            setDialogLoading(false); 
            // Reload user data
          // await loadUserData();  
            
            toast.success(`KYC Form 3 submitted successfully!`);
          } else {
            throw new Error('Transaction failed');
          }

       } else {
          throw new Error('Transaction failed');
      }
       
     } catch (err: any) {
        console.error('KYC Level 3 submission failed:', err);
        setDialogLoading(false);
        
        let errorMessage = 'Submission failed';
        if (err.code === 4001) {
          errorMessage = 'User cancelled transaction';
        } else if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient balance';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setDialogError(errorMessage);
        toast.error(errorMessage);
      }
   };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setError('');
              setLoading(true);
              loadUserData();
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

// console.log("funny_dog77:",userKYCStatus);

const checkauthButton = async () => {  
  if(provider&&account){   
      const signer = provider?.getSigner(); 
     if(signer) loginauthkycuser(account,[1,2,3],signer,ownerAddress); 
   } 
}  

  return (
    <>
    {!authjwk||!activeuser||!isAddressEqual(activeuser, account)?(
                <>
                   <Checksign  switchlogin={checkauthButton} />
                </>
              ):(
              <>
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          KYC Data Management
        </Typography>
        
        {/* 用户信息显示 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            User Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Wallet Address
                  </Typography>
                  <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                    {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Not Connected'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Your Token ID
                  </Typography>
                  <Typography variant="h6">
                    {userTokenId || 'None'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* KYC状态显示 */}
        {userKYCStatus.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              KYC Status
            </Typography>
            <Grid container spacing={2}>
              {userKYCStatus.map((status) => (
                <Grid item xs={12} md={4} key={status.templateId}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        KYC Form {status.templateId}
                      </Typography>
                      <Chip
                       label={getKYCStatusText(status.status)}
                        color={getKYCStatusColor(status.status)}
                        size="small"
                      />
                      {status.submittedAt && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Submitted: {status.submittedAt.toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* KYC表单Tabs */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="KYC form tabs">
              <Tab label="Basic Info (Form1)" {...a11yProps(1)} />
              <Tab label="Identity Verification (Form2)" {...a11yProps(2)} disabled={!userTokenId} />
              <Tab label="Video Verification (Form3)" {...a11yProps(3)} disabled={!userTokenId} />
            </Tabs>
          </Box>
          
          {/* 基本信息表单 */}
          <TabPanel value={activeTab} index={0}>
            {!userTokenId ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Fill in basic information and submit to mint your KYC Form 1
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                You already have KYC, you can update your basic information
              </Alert>
            )}
            
            <BasicInfoForm
                  KYCStatus={userKYCStatus?.find(t => t.templateId === 1)?.status}
                  initialData={basicInfo}
                  onSubmit={handleProfileNFT}
                  submitting={loading || dialogLoading}
                />
          </TabPanel>
          
          {/* 身份认证表单 */}
          <TabPanel value={activeTab} index={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please fill in your real identity information for verification
            </Alert>
            
            <KYCLevel1Form
                   KYCStatus={userKYCStatus?.find(t => t.templateId === 2)?.status}
                   initialData={kycLevel1}
                   onSubmit={handleMintWithKYCLevel1}
                   submitting={loading || dialogLoading}
                 />
          </TabPanel>
          
          {/* 视频认证表单 */}
          <TabPanel value={activeTab} index={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please upload a video containing the random number for advanced verification
            </Alert>
            
            <KYCLevel2Form
                   KYCStatus={userKYCStatus?.find(t => t.templateId === 3)?.status}
                   initialData={kycLevel2}
                   onSubmit={handleMintWithKYCLevel2}
                   submitting={loading || dialogLoading}
                 />
          </TabPanel>
        </Box>
      </Paper>
      
      {/* 步骤器弹窗 */}
      <SignMessageDialog onClose={endsubmitjob} />
    </Box>
    </>
    )}
   </>
  );
};

export default AssetsMintPage;